import React, { useEffect, useState } from 'react';
import { Keyboard, Command as CommandIcon } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';

/**
 * Discoverable keyboard-shortcut overlay for Mission Control.
 *
 * Opens with `?` from anywhere — mirrors the Mirror surface so the
 * cross-product idiom is consistent. ESC dismisses (handled by shadcn).
 *
 * Lives in DashboardLayout so it is always available on Mission Control
 * pages. The Mirror surface has its own equivalent inside PIELayout.
 */
const SHORTCUT_GROUPS = [
  {
    title: 'Cross-surface (press g, then…)',
    rows: [
      ['g · m', 'Open Seertech Mirror'],
      ['g · h', 'Mission Control home'],
    ],
  },
  {
    title: 'Command palette',
    rows: [
      ['⌘ · K', 'Search & jump anywhere'],
      ['Ctrl · K', '…on Windows / Linux'],
    ],
  },
  {
    title: 'General',
    rows: [
      ['?', 'Open this help'],
      ['Esc', 'Close popovers / modals'],
    ],
  },
];

function isEditable(el) {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  return false;
}

export default function MissionControlShortcuts() {
  const [open, setOpen] = useState(false);

  // Listen for `?` outside any editable element.
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditable(document.activeElement)) return;
      // `?` is Shift+/. Different browsers/OS layouts surface it as either
      // `e.key === '?'` directly, or `e.key === '/'` with `e.shiftKey`. Accept both.
      const isQuestion = e.key === '?' || (e.key === '/' && e.shiftKey);
      if (isQuestion) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-lg"
        data-testid="mc-shortcuts-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Keyboard className="h-4 w-4 text-[#394fd4]" />
            Keyboard shortcuts
          </DialogTitle>
          <DialogDescription>
            Same idiom across Mission Control and Mirror. Tap{' '}
            <kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-[11px] font-mono">?</kbd>{' '}
            anywhere to re-open.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {SHORTCUT_GROUPS.map((g) => (
            <div key={g.title}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                {g.title}
              </div>
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-md overflow-hidden">
                {g.rows.map(([combo, label]) => (
                  <div
                    key={combo}
                    className="flex items-center justify-between px-3 py-2 bg-white"
                  >
                    <span className="text-sm text-slate-700">{label}</span>
                    <kbd className="font-mono text-[11px] tracking-tight px-2 py-1 rounded bg-slate-100 text-slate-800 border border-slate-200">
                      {combo}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="pt-2 text-[11px] text-slate-500 inline-flex items-center gap-1">
          <CommandIcon className="h-3 w-3" /> More navigation lives inside the command palette.
        </div>
      </DialogContent>
    </Dialog>
  );
}
